/*
 * FORKED from Horizon 4.1.5 (docs/FORK-REGISTER.md, OPEN-ITEMS OI-135).
 *
 * The vendor dialog stacked every image at the full viewport width in one
 * vertical scroll — a 1440px-wide crop of the first image, a black product
 * shot the size of the screen under it — closed when the shopper clicked the
 * image they meant to inspect, and on a phone opened each image already at
 * 1.5x with no way to swipe to the next one. This is a lightbox instead: one
 * image at a time, whole, on white; a counter, a zoom toggle and a close
 * button; arrows, a thumbnail strip, swipe. A click zooms to the point clicked
 * and the image follows the pointer; on touch, double-tap or pinch zooms and a
 * drag pans. The public surface the gallery calls — `open(index, event)`,
 * `close()`, `loadHighResolutionImage(element)`, `refs.media`, the
 * `zoom-media:selected` event — is the vendor's.
 */
import { Component } from '@theme/component';
import { clamp, debounce, lockScroll, unlockScroll, prefersReducedMotion } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const ZOOM_SCALE = 2.5;
const MAX_SCALE = 4;
const DOUBLE_TAP_DELAY = 300;
const TAP_SLOP = 10;

/**
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} track - The horizontal scroller holding one slide per media.
 * @property {HTMLElement[]} media - The slides.
 * @property {HTMLElement} [current] - The counter's current number.
 * @property {HTMLButtonElement} [zoomToggle] - The toolbar's zoom button.
 * @property {HTMLButtonElement} [previous] - The previous arrow.
 * @property {HTMLButtonElement} [next] - The next arrow.
 * @property {HTMLElement} [thumbnails] - The thumbnail strip.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'track', 'media'];

  #index = 0;
  #scale = 1;
  #x = 0;
  #y = 0;

  /** @type {Map<number, {x: number, y: number}>} */
  #pointers = new Map();
  /** @type {{scale: number, x: number, y: number, distance: number, midX: number, midY: number, startX: number, startY: number, moved: boolean} | null} */
  #gesture = null;
  #lastTap = { time: 0, x: 0, y: 0 };
  #pointerType = 'mouse';

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {Element | null} */
  #opener = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog, track } = this.refs;

    dialog.addEventListener('close', this.#handleClose, { signal });
    track.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    track.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    track.addEventListener('pointermove', this.#handlePointerMove, { signal });
    track.addEventListener('pointerup', this.#handlePointerUp, { signal });
    track.addEventListener('pointercancel', this.#handlePointerUp, { signal });
    track.addEventListener('click', this.#handleClick, { signal });
    window.addEventListener('resize', this.#handleResize, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Opens the lightbox on a media item.
   *
   * @param {number} index - The index of the media to show.
   * @param {Event} [event] - The event that opened it.
   */
  open(index, event) {
    event?.preventDefault();

    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#opener = document.activeElement;
    dialog.showModal();
    // Focus the dialog, not its first button: no ring on a mouse open, and Tab
    // still reaches the toolbar first.
    dialog.focus({ preventScroll: true });
    lockScroll(dialog);
    this.select(Number(index) || 0, { instant: true, force: true });
  }

  /**
   * Closes the lightbox; the `close` event does the clean-up, so Escape and
   * this button end the same way.
   */
  close() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    if (prefersReducedMotion()) return dialog.close();

    dialog.classList.add('admira-lightbox--closing');
    setTimeout(() => {
      dialog.classList.remove('admira-lightbox--closing');
      dialog.close();
    }, 160);
  }

  #handleClose = () => {
    this.#resetZoom(false);
    unlockScroll(this.refs.dialog);
    window.dispatchEvent(new DialogCloseEvent());

    if (this.#opener instanceof HTMLElement && this.#opener.isConnected && this.#opener !== document.body) {
      this.#opener.focus({ preventScroll: true });
    }
    this.#opener = null;
  };

  previous() {
    this.select(this.#index - 1);
  }

  next() {
    this.select(this.#index + 1);
  }

  /**
   * @param {number} index - The thumbnail's media index.
   */
  handleThumbnailClick(index) {
    this.select(Number(index));
  }

  /**
   * Shows a media item.
   *
   * @param {number} index - The index to show.
   * @param {{instant?: boolean, force?: boolean}} [options]
   */
  select(index, { instant = false, force = false } = {}) {
    const { media, track } = this.refs;
    const target = clamp(index, 0, media.length - 1);
    const slide = media[target];
    if (!slide) return;

    this.#resetZoom(false);
    track.scrollTo({
      left: slide.offsetLeft,
      behavior: instant || prefersReducedMotion() ? 'instant' : 'smooth',
    });
    this.#setIndex(target, force);
  }

  /**
   * The vendor gallery preloads the zoomed image on hover; here the lightbox's
   * own srcset covers it, so this only asks the browser for the slide early.
   *
   * @param {HTMLElement} slide - A slide from `refs.media`.
   */
  loadHighResolutionImage(slide) {
    const image = slide?.querySelector('img');
    if (image) image.loading = 'eager';
  }

  /** Zooms the current image in at its centre, or back out. */
  toggleZoom() {
    if (this.#scale > 1) return this.#resetZoom(true);
    this.#zoomAt(0, 0, ZOOM_SCALE);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previous();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
      case '+':
      case '=':
        event.preventDefault();
        if (this.#scale === 1) this.#zoomAt(0, 0, ZOOM_SCALE);
        break;
      case '-':
        event.preventDefault();
        this.#resetZoom(true);
        break;
      case 'Escape':
        // The first Escape leaves the zoom, the second closes.
        if (this.#scale > 1) {
          event.preventDefault();
          this.#resetZoom(true);
        }
        break;
    }
  }

  /**
   * @param {number} index
   * @param {boolean} force - Refresh the chrome even when the index is unchanged.
   */
  #setIndex(index, force = false) {
    if (index === this.#index && !force) return;
    this.#index = index;

    const { media, current, previous, next, thumbnails, zoomToggle } = this.refs;
    const slide = media[index];

    if (current) current.textContent = String(index + 1);
    if (previous) previous.disabled = index === 0;
    if (next) next.disabled = index === media.length - 1;
    if (zoomToggle) zoomToggle.disabled = !this.#imageOf(slide);

    for (const [i, item] of media.entries()) {
      item.toggleAttribute('inert', i !== index);
      if (i === index) this.loadHighResolutionImage(item);
    }

    if (thumbnails) {
      const buttons = Array.from(thumbnails.querySelectorAll('button'));
      for (const [i, button] of buttons.entries()) {
        if (i === index) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      }
      const thumbnail = buttons[index];
      if (thumbnail) {
        thumbnails.scrollTo({
          left: thumbnail.offsetLeft - (thumbnails.clientWidth - thumbnail.offsetWidth) / 2,
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        });
      }
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  #handleScroll = debounce(() => {
    if (this.#scale > 1) return;
    const { track } = this.refs;
    if (!track.clientWidth) return;
    this.#setIndex(clamp(Math.round(track.scrollLeft / track.clientWidth), 0, this.refs.media.length - 1));
  }, 60);

  #handleResize = debounce(() => {
    if (!this.refs.dialog.open) return;
    this.#resetZoom(false);
    const slide = this.refs.media[this.#index];
    if (slide) this.refs.track.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
  }, 100);

  /* ---- Zoom geometry ------------------------------------------------------ */

  /**
   * @param {Element | undefined} slide
   * @returns {HTMLImageElement | null}
   */
  #imageOf(slide) {
    const image = slide?.querySelector('img.admira-lightbox__image');
    return image instanceof HTMLImageElement ? image : null;
  }

  get #image() {
    return this.#imageOf(this.refs.media[this.#index]);
  }

  /**
   * The drawn picture inside the `object-fit: contain` box.
   * @param {HTMLImageElement} image
   */
  #frame(image) {
    const boxWidth = image.clientWidth;
    const boxHeight = image.clientHeight;
    const naturalWidth = image.naturalWidth || Number(image.getAttribute('width')) || boxWidth;
    const naturalHeight = image.naturalHeight || Number(image.getAttribute('height')) || boxHeight;
    const ratio = naturalWidth / naturalHeight || 1;

    let width = boxWidth;
    let height = boxWidth / ratio;
    if (height > boxHeight) {
      height = boxHeight;
      width = boxHeight * ratio;
    }

    return { width, height, boxWidth, boxHeight };
  }

  /**
   * A client point relative to the image box's centre. Measured on the slide,
   * whose padding is symmetric so its centre is the image's: the image's own
   * rect includes the zoom transform, and measuring against it fed every pan
   * back into the next — the picture flipped between top and bottom as the
   * pointer crossed the middle.
   * @param {HTMLImageElement} image
   * @param {number} clientX
   * @param {number} clientY
   */
  #fromCentre(image, clientX, clientY) {
    const rect = (image.closest('.admira-lightbox__slide') ?? image).getBoundingClientRect();
    return { x: clientX - rect.left - rect.width / 2, y: clientY - rect.top - rect.height / 2 };
  }

  /**
   * Whether a client point lands on the drawn picture rather than the white
   * around it.
   * @param {HTMLImageElement} image
   * @param {number} clientX
   * @param {number} clientY
   */
  #hitsPicture(image, clientX, clientY) {
    const { width, height } = this.#frame(image);
    const { x, y } = this.#fromCentre(image, clientX, clientY);
    return Math.abs(x) <= width / 2 && Math.abs(y) <= height / 2;
  }

  /**
   * @param {number} scale
   * @param {number} x
   * @param {number} y
   */
  #clampPan(scale, x, y) {
    const image = this.#image;
    if (!image) return { x: 0, y: 0 };
    const { width, height, boxWidth, boxHeight } = this.#frame(image);
    const maxX = Math.max(0, (width * scale - boxWidth) / 2);
    const maxY = Math.max(0, (height * scale - boxHeight) / 2);
    return { x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
  }

  /**
   * Zooms so the point (relative to the image centre) stays under the pointer.
   * @param {number} pointX
   * @param {number} pointY
   * @param {number} scale
   * @param {boolean} [animate]
   */
  #zoomAt(pointX, pointY, scale, animate = true) {
    const image = this.#image;
    if (!image) return;

    const next = clamp(scale, 1, MAX_SCALE);
    const factor = next / this.#scale;
    const pan = this.#clampPan(next, pointX - (pointX - this.#x) * factor, pointY - (pointY - this.#y) * factor);

    if (next > 1 && this.#scale === 1) this.#upgradeResolution(image, next);

    this.#scale = next;
    this.#x = pan.x;
    this.#y = pan.y;
    this.#apply(animate);
  }

  /**
   * Asks the srcset for a candidate as wide as the zoomed picture.
   * @param {HTMLImageElement} image
   * @param {number} scale
   */
  #upgradeResolution(image, scale) {
    const wanted = Math.ceil(image.clientWidth * Math.max(scale, ZOOM_SCALE));
    if (Number.parseInt(image.sizes, 10) >= wanted) return;
    image.sizes = `${wanted}px`;
  }

  /**
   * Maps the pointer across the image box onto the pan range, so the corner
   * under the pointer is the corner on show.
   * @param {HTMLImageElement} image
   * @param {number} clientX
   * @param {number} clientY
   * @param {boolean} animate
   */
  #follow(image, clientX, clientY, animate) {
    const { width, height, boxWidth, boxHeight } = this.#frame(image);
    const { x, y } = this.#fromCentre(image, clientX, clientY);
    const maxX = Math.max(0, (width * this.#scale - boxWidth) / 2);
    const maxY = Math.max(0, (height * this.#scale - boxHeight) / 2);
    this.#x = clamp((-x / (width / 2)) * maxX, -maxX, maxX);
    this.#y = clamp((-y / (height / 2)) * maxY, -maxY, maxY);
    this.#apply(animate);
  }

  /** @param {boolean} animate */
  #resetZoom(animate) {
    if (this.#scale === 1 && this.#x === 0 && this.#y === 0) return;
    this.#scale = 1;
    this.#x = 0;
    this.#y = 0;
    this.#apply(animate);
  }

  /** @param {boolean} animate */
  #apply(animate) {
    const { dialog, zoomToggle } = this.refs;
    const image = this.#image;
    const zoomed = this.#scale > 1;

    if (image) {
      image.style.transition = animate && !prefersReducedMotion() ? '' : 'none';
      image.style.transform = zoomed ? `translate3d(${this.#x}px, ${this.#y}px, 0) scale(${this.#scale})` : '';
    }

    // A reset can come after the slide changed; clear any other slide too.
    for (const slide of this.refs.media) {
      const other = this.#imageOf(slide);
      if (other && other !== image && other.style.transform) {
        other.style.transition = 'none';
        other.style.transform = '';
      }
    }

    dialog.toggleAttribute('data-zoomed', zoomed);
    if (zoomToggle) {
      zoomToggle.setAttribute('aria-pressed', String(zoomed));
      const label = zoomed ? zoomToggle.dataset.labelOut : zoomToggle.dataset.labelIn;
      if (label) zoomToggle.setAttribute('aria-label', label);
    }
  }

  /* ---- Pointer input ------------------------------------------------------ */

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    this.#pointerType = event.pointerType;
    if (event.pointerType === 'mouse') return;

    const image = this.#image;
    if (!image || !(event.target instanceof Node) || !this.refs.media[this.#index]?.contains(event.target)) return;

    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.#pointers.size === 2) {
      const [a, b] = [...this.#pointers.values()];
      if (!a || !b) return;
      const mid = this.#fromCentre(image, (a.x + b.x) / 2, (a.y + b.y) / 2);
      this.#gesture = {
        scale: this.#scale,
        x: this.#x,
        y: this.#y,
        distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        midX: mid.x,
        midY: mid.y,
        startX: 0,
        startY: 0,
        moved: true,
      };
      this.refs.dialog.setAttribute('data-gesture', '');
    } else if (this.#pointers.size === 1) {
      this.#gesture = {
        scale: this.#scale,
        x: this.#x,
        y: this.#y,
        distance: 0,
        midX: 0,
        midY: 0,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
      };
      if (this.#scale > 1) {
        image.setPointerCapture?.(event.pointerId);
        this.refs.dialog.setAttribute('data-gesture', '');
      }
    }
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    const image = this.#image;
    if (!image) return;

    // Mouse: once zoomed, the picture follows the pointer across the stage.
    if (event.pointerType === 'mouse') {
      if (this.#scale > 1) this.#follow(image, event.clientX, event.clientY, false);
      return;
    }

    if (!this.#pointers.has(event.pointerId) || !this.#gesture) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const gesture = this.#gesture;

    if (this.#pointers.size >= 2) {
      const [a, b] = [...this.#pointers.values()];
      if (!a || !b) return;
      const scale = clamp((gesture.scale * Math.hypot(a.x - b.x, a.y - b.y)) / gesture.distance, 1, MAX_SCALE);
      const mid = this.#fromCentre(image, (a.x + b.x) / 2, (a.y + b.y) / 2);
      const factor = scale / gesture.scale;
      // Keep the pinch's starting midpoint under the fingers' current midpoint.
      const pan = this.#clampPan(
        scale,
        mid.x - (gesture.midX - gesture.x) * factor,
        mid.y - (gesture.midY - gesture.y) * factor
      );
      if (scale > 1 && this.#scale === 1) this.#upgradeResolution(image, scale);
      this.#scale = scale;
      this.#x = pan.x;
      this.#y = pan.y;
      this.#apply(false);
      return;
    }

    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    if (Math.hypot(dx, dy) > TAP_SLOP) gesture.moved = true;

    if (this.#scale > 1) {
      const pan = this.#clampPan(this.#scale, gesture.x + dx, gesture.y + dy);
      this.#x = pan.x;
      this.#y = pan.y;
      this.#apply(false);
    }
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (event.pointerType === 'mouse' || !this.#pointers.has(event.pointerId)) return;

    const gesture = this.#gesture;
    const wasSingle = this.#pointers.size === 1;
    this.#pointers.delete(event.pointerId);

    if (this.#pointers.size > 0) {
      // One finger lifted out of a pinch: carry on as a pan from here.
      const [rest] = [...this.#pointers.values()];
      if (rest) {
        this.#gesture = {
          scale: this.#scale,
          x: this.#x,
          y: this.#y,
          distance: 0,
          midX: 0,
          midY: 0,
          startX: rest.x,
          startY: rest.y,
          moved: true,
        };
      }
      return;
    }

    this.#gesture = null;
    this.refs.dialog.removeAttribute('data-gesture');

    // A pinch that ends near 1x settles back to the whole picture.
    if (this.#scale < 1.1) this.#resetZoom(true);

    if (!wasSingle || !gesture || gesture.moved || event.type === 'pointercancel') return;

    const now = performance.now();
    const isDoubleTap =
      now - this.#lastTap.time < DOUBLE_TAP_DELAY &&
      Math.hypot(event.clientX - this.#lastTap.x, event.clientY - this.#lastTap.y) < TAP_SLOP * 4;

    if (!isDoubleTap) {
      this.#lastTap = { time: now, x: event.clientX, y: event.clientY };
      return;
    }

    this.#lastTap = { time: 0, x: 0, y: 0 };
    const image = this.#image;
    if (!image) return;
    if (this.#scale > 1) return this.#resetZoom(true);
    const point = this.#fromCentre(image, event.clientX, event.clientY);
    this.#zoomAt(point.x, point.y, ZOOM_SCALE);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (this.#pointerType !== 'mouse') return;

    const slide = this.refs.media[this.#index];
    if (!slide || !(event.target instanceof Node) || !slide.contains(event.target)) return;

    const image = this.#image;
    if (!image) return;

    if (this.#scale > 1) return this.#resetZoom(true);

    // The white around the picture closes, as a backdrop would.
    if (!this.#hitsPicture(image, event.clientX, event.clientY)) return this.close();

    this.#upgradeResolution(image, ZOOM_SCALE);
    this.#scale = ZOOM_SCALE;
    this.#follow(image, event.clientX, event.clientY, true);
  };
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
